import { z } from 'zod';
const marketingAudiences = ['FAMILLE', 'COUPLE', 'SENIOR', 'GROUPE'];
export const marketingChannels = [
    'wordpress',
    'linkedin',
    'facebook',
    'instagram',
    'tiktok',
];
/**
 * Bornes hautes des champs libres du brief. Ils finissent tels quels dans un
 * prompt facturé au token : sans plafond, un copier-coller malheureux dans
 * « Sujet » partait en génération et se payait. Calées sur ce que le dépôt
 * borne déjà — 255 pour un intitulé (titre d'événement, nom de société), 1 000
 * pour un texte de contexte (description d'événement) — et non sur des chiffres
 * inventés.
 */
// Exportées : le formulaire du back-office rejoue les mêmes bornes sous le
// champ. Sans elles, un sujet trop long partait au serveur et revenait en toast
// générique, loin du champ à raccourcir.
export const BRIEF_LABEL_MAX = 255;
export const BRIEF_TEXT_MAX = 1000;
export const createMarketingContentSchema = z.object({
    // Question de visibilité IA que cet article vise, quand le brief naît d'une
    // recommandation. Sans ce champ ici, zod retirerait la clé en silence et le
    // lien serait perdu entre la modale et la base.
    aeoPromptId: z.string().optional(),
    // Absent = l'IA choisit elle-même un angle éditorial distinctif
    angle: z
        .string()
        .max(BRIEF_TEXT_MAX, `L’angle ne peut pas dépasser ${BRIEF_TEXT_MAX} caractères`)
        .optional(),
    // Absent = tous publics
    audience: z.enum(marketingAudiences).optional(),
    channels: z.array(z.enum(marketingChannels)).min(1),
    // Absent = post d'inspiration sans offre commerciale
    offerUrl: z.string().url().optional(),
    // Contexte (période, public…) désormais fondu dans `angle` — conservé optionnel
    // pour les contenus existants.
    period: z
        .string()
        .max(BRIEF_LABEL_MAX, `La période ne peut pas dépasser ${BRIEF_LABEL_MAX} caractères`)
        .optional(),
    // Page déjà en ligne à reprendre (conseil REWRITE). Absent = page neuve.
    // Même piège que `aeoPromptId` ci-dessus : sans la clé déclarée ici, zod la
    // retirerait en silence et la cible serait perdue entre la modale et la base.
    revisePageUrl: z.string().url().optional(),
    subject: z
        .string()
        .min(1)
        .max(BRIEF_LABEL_MAX, `Le sujet ne peut pas dépasser ${BRIEF_LABEL_MAX} caractères`),
});
export const updateMarketingContentSchema = z.object({
    articleHtml: z.string().optional(),
    articleMetaDescription: z.string().optional(),
    articleSlug: z.string().optional(),
    articleTitle: z.string().optional(),
    // La version du contenu que l'appelant croit modifier — le `updatedAt` reçu
    // avec la ligne (déjà rendu par toutes les réponses). L'auto-sauvegarde
    // renvoie l'instantané COMPLET des huit champs générés à chaque frappe : deux
    // onglets ouverts, ou deux relecteurs, s'écrasaient intégralement et en
    // silence. Le serveur refuse le PATCH quand la ligne a bougé depuis.
    //
    // Optionnel, et il doit le rester : les appels serveur internes et les
    // clients déployés avant ce champ continuent de passer sans lui — le
    // verrouillage est alors simplement absent, comme avant.
    expectedUpdatedAt: z.string().datetime().optional(),
    facebook: z.string().optional(),
    // Sélection d'images par canal : ids d'images EXCLUES du post de chaque
    // réseau (une nouvelle image est active partout par défaut)
    hiddenImages: z.record(z.enum(marketingChannels), z.array(z.string())).optional(),
    instagram: z.string().optional(),
    linkedin: z.string().optional(),
    tiktok: z.string().optional(),
});
export const sendMarketingContentSchema = z.object({
    // Override par canal, prioritaire sur scheduleDate. Ignoré pour tiktok
    // (toujours brouillon : la vidéo reste à produire).
    channelDates: z.record(z.enum(marketingChannels), z.string().datetime()).optional(),
    // Canaux à envoyer (sélection de la modale). Le serveur ignore ceux qui ne
    // sont plus dans `content.channels` : une modale ouverte avant qu'on retire
    // un canal ne doit pas le publier quand même.
    channels: z.array(z.enum(marketingChannels)).min(1),
    // Mettre l'article EN LIGNE tout de suite, au lieu du brouillon ou de la
    // programmation. Prioritaire sur `scheduleDate` et `channelDates`.
    //
    // Troisième état indispensable : `scheduleDate` absente veut dire brouillon et
    // présente veut dire programmé — aucune combinaison ne disait « publie
    // maintenant », et l'article restait donc un brouillon que personne n'allait
    // sortir à la main. Ne concerne que `wordpress` : Postiz ne sait que déposer
    // un brouillon ou planifier, publier tout de suite s'y fait en choisissant
    // l'instant présent comme date.
    //
    // Absent = comportement d'avant, inchangé pour les clients déjà déployés.
    publishNow: z.boolean().optional(),
    // Absent = brouillon Postiz ; présent = publication planifiée à cette date (ISO)
    scheduleDate: z.string().datetime().optional(),
});
// Champs texte accompagnant l'upload d'une image (`POST
// /marketing-content/:id/images`). Requête multipart : toutes les valeurs
// arrivent en chaînes, y compris `watermark` ('false' = case décochée, toute
// autre valeur ou absence = filigrane appliqué).
export const addMarketingContentImageSchema = z.object({
    subtitle: z.string().max(500).optional(),
    title: z.string().max(500).optional(),
    watermark: z.string().max(10).optional(),
});
// Ajout d'un canal oublié au brief : le cron génère son contenu via le
// circuit de révision (les contenus existants sont conservés)
export const addMarketingContentChannelSchema = z.object({
    channel: z.enum(marketingChannels),
});
// La consigne de révision part elle aussi dans le prompt de régénération : même
// plafond que l'angle, c'est le même registre de texte libre.
export const regenerateMarketingContentSchema = z.object({
    instruction: z
        .string()
        .min(1)
        .max(BRIEF_TEXT_MAX, `La consigne ne peut pas dépasser ${BRIEF_TEXT_MAX} caractères`),
});
// `force` = le relecteur a vu que la page avait bougé et applique quand même.
// Sans ce drapeau, l'action refuserait indéfiniment une reprise dont la page a
// changé, sans lui offrir d'issue.
export const markMarketingContentAppliedSchema = z.object({
    force: z.boolean().optional(),
});
// Génération d'image libre (onglet « Images ») : un prompt, un format, hors de
// tout contenu. Tailles acceptées telles quelles par gpt-image-2 (multiples de 16).
export const marketingImageSizes = ['1200x1600', '1024x1024', '1536x1024'];
export const generateStandaloneImageSchema = z.object({
    prompt: z.string().trim().min(1).max(4000),
    size: z.enum(marketingImageSizes),
});
// Historique de l'onglet « Images » : page suivante (`cursor` = id de la
// dernière image reçue) et recherche dans le prompt.
export const listGeneratedImagesSchema = z.object({
    cursor: z.string().optional(),
    search: z.string().trim().max(200).optional(),
});
// Réécriture ciblée : le relecteur sélectionne un passage, l'IA réécrit
// uniquement ce passage (le texte complet ne sert que de contexte)
export const rewriteMarketingSelectionSchema = z.object({
    channel: z.enum(marketingChannels),
    fullText: z.string().min(1),
    // Absente = réécriture par défaut (corriger, fluidifier)
    instruction: z.string().optional(),
    selectedText: z.string().min(1),
});
// Édition des textes incrustés à la relecture : titre de campagne (niveau
// contenu) + titre/sous-titre/activation par image. Le serveur re-compose
// l'overlay des images concernées.
export const updateMarketingImageOverlaysSchema = z.object({
    // Absent = ne pas toucher au titre de campagne
    campaignTitle: z.string().optional(),
    images: z.array(z.object({
        id: z.string(),
        overlayEnabled: z.boolean(),
        subtitle: z.string().optional(),
        title: z.string().optional(),
    })),
});
// Génération d'images : 1 à 4 sujets (une image par sujet). Chaque sujet est
// injecté dans le prompt luxe côté serveur.
export const MAX_IMAGE_SUBJECTS = 6;
export const generateMarketingImagesSchema = z.object({
    subjects: z.array(z.string().trim().min(1)).min(1).max(MAX_IMAGE_SUBJECTS),
});
